import './map.css';
import { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from "motion/react"
import SubwayLine from './animations/subway-line';
import StationHoverCard from './components/StationHoverCard';

function Map({ allowMultipleLines = true }) {
  const [expandedStations, setExpandedStations] = useState(new Set());
  const [activeBranch, setActiveBranch] = useState(null);
  const [activeBranches, setActiveBranches] = useState(new Set());
  const svgRef = useRef(null);

  const containerRef = useRef(null);
  const [hoveredStation, setHoveredStation] = useState(null);
  const [clickedBranchStation, setClickedBranchStation] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [branchModalData, setBranchModalData] = useState(null);
  const [mainStationModalData, setMainStationModalData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const hoverCardRefs = useRef({});
  const labelRefs = useRef({});

  const [mainStationPositions, setMainStationPositions] = useState({});
  const [visibleStations, setVisibleStations] = useState(new Set());
  const [branchStationPositions, setBranchStationPositions] = useState({});
  const [visibleBranchStations, setVisibleBranchStations] = useState(new Set());
  
  // Viewport dimensions and viewBox
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [viewBox, setViewBox] = useState(`0 0 ${window.innerWidth} ${window.innerHeight}`);
  const [mainLinePath, setMainLinePath] = useState('');
  const heraldIcon = require('./assets/images/herald.png');
  const havenIcon = require('./assets/images/haven.jpg');
  const altaIcon = require('./assets/images/alta.png');
  const jetblueIcon = require('./assets/images/jetblue.png');
  const iheartmediaIcon = require('./assets/images/iheartmedia.png');
  const conedisonIcon = require('./assets/images/coned.png');
  const developerPortalImg = require('./assets/images/dev-portal.jpg');
  const heraldServerImg = require('./assets/images/heraldserver.jpg');
  const coverpathAdvisorToolsImg = require('./assets/images/coverpath.webp');
  const altaImg = require('./assets/images/alta-img.png');
  const jetblueImg = require('./assets/images/jetblue-img.png');
  const jetblueImgOld = require('./assets/images/jetblue-img-old.png');
  const blueEyeImg = require('./assets/images/blueeye-img.png');
  const ifsaImg = require('./assets/images/ifsa-img.webp');
  const allergroImg = require('./assets/images/allergro-img.jpeg');
  const rcsImg = require('./assets/images/rcslogosvg.png');
  const unifiedApiImg = require('./assets/images/unifiedapi.png');
  const ycombinatorImg = require('./assets/images/ycombinator.jpg');
  const doddFrankImg = require('./assets/images/dodd-frank.webp');
  const mmImg = require('./assets/images/mm.jpg');
  const oilWebScrapperImg = require('./assets/images/web-scraping.png');
  const componentLibraryImg = require('./assets/images/angular-component.webp');
  const engineeringManagerImg = require('./assets/images/Engineering_manager.png');
  // const identityServiceImg = require('./assets/images/identityservice.jpg');
  const stations = [
    {
      id: 'herald-api',
      name: 'Herald API',
      timeFrame: 'June 2021 - Present',
      icon: heraldIcon,
      positions: [{title: 'Founding Engineer', timeFrame: 'June 2021 - Sept 2023'}, {title: 'Engineering Manager', timeFrame: 'Sept 2023 - Present'}],
      details: 'Herald API is a Series A startup that provides a platform for quoting insurance policies. It allows brokers to send a single API request and receive quotes from multiple insurance carriers, saving them time and money. I joined as a founding engineer and was promoted to Engineering Manager, where I recruited and managed a team.',
      position: 200,
      branches: [
        { name: 'Developer Portal', img: developerPortalImg, details: 'Developer Portal is a web application built in Angular that allows internal employees to manager customers. It also allows customers to view their quotes, applications and brokers that they are sending data through our system.', length: 150 },
        { name: 'Herald Request Builder (HeRB)', img: heraldServerImg, details: 'A core feature of Herald was our /applications endpoint, which allowed us to dynamically build an application based on the selected insurance products. HeRB was designed to showcase that endpoint and allow developers to test it and see how an implementation might work. Based on the questions you answered, you would receive additional questions in return and the front end could dynamically build those questions and provide you with a quote for each product. This also allowed rigerous internal testing of our endpoint and product configuration. I built the entire feature from beginning to end.', length: 120 },
        { name: 'Unifed Insurance Quoting API', img: unifiedApiImg,  details: 'Herald\'s main feature was its unified API. This enabled users to submit questions to our /applications or /submissions endpoints in our Herald format. Our API would then transform those questions using internal logic and route the requests to various carrier APIs. These APIs could be REST, SOAP, or more recently email-based with automated responses. This saved brokers a significant amount of time. I worked on many features for these APIs as well as coding various insurance products into the translation layer.', length: 180 },
        { name: 'Engineering Manager', img: engineeringManagerImg, details: 'I was promoted to Engineering Manager without HR support. I built our hiring process, onboarding program, and training framework from the ground up. I hired and managed a team of three engineers, providing mentorship and technical direction. I helped develop their careers and foster growth while delivering features on tight deadlines.', length: 200 }
      ]
    },
    {
      id: 'haven-technologies',
      name: 'Haven Technologies',
      timeFrame: 'April 2020 - June 2021',
      icon: havenIcon,
      positions: [{title: 'Senior Frontend Engineer', timeFrame: 'April 2020 - June 2021'}],
      details: 'Haven Technologies is a startup wholly owned by MassMutual. I joined as a Senior Frontend Engineer on the Advisor Tools team, building experiences for insurance advisors. Haven Technologies was unfortunately shut down and absorbed by MassMutual.',
      position: 400,
      branches: [
        { name: 'Coverpath Advisor Tools', img: coverpathAdvisorToolsImg, details:'While working at HavenLife, I worked on a product called Coverpath. Coverpath helped advisors deliver insurance quotes to their clients quickly by providing personalized links. Clients could follow these links, fill out their information, and receive a quote for their life insurance policy. Coverpath was built on Angular with a backend built on a custom internal framework called Amorphic.', length: 140 },
        { name: 'Component Library', img: componentLibraryImg, details: `When I started working here, I realized that every time we needed a button, control, or other UI element, we either copy-pasted code or created something that looked similar but wasn't consistent. I worked cross-functionally to establish a group of engineers from all teams to build a component library hosted in Storybook that could be used across the company. This led to faster development and significantly cleaner code overall. We tracked the impact and measured a 30% reduction in engineering time per sprint—not even factoring in the ongoing maintenance that was now eliminated.`, length: 160 },
      ]
    },
    {
      id: 'alta-saas',
      name: 'Alta',
      timeFrame: 'July 2019 - April 2020',
      icon: altaIcon,
      positions: [{title: 'CTO / Co-Founder', timeFrame: 'July 2019 - April 2020'}],
      details: 'Alta was a Vendor Management System for companies to manage relationships with vendors. I co-founded the company and served as CTO, building it from the ground up. Alta was in discussions with companies like Walmart, Amazon, and Time Warner. Unfortunately, all conversations fell through after COVID-19, forcing us to pursue other opportunities.',
      position: 600,
      branches: [
        { name: 'Co-Founder and CTO', img: altaImg, details: 'As co-founder and CTO, I architected and built Alta. It featured an Angular front end and Django backend. The platform allowed companies to onboard all their vendors and manage contracts, communications, and support tickets in one centralized location.', length: 170 },
        { name: 'Y-Combinator', img: ycombinatorImg, details: `After receiving initial interest from companies like Walmart and Amazon, we secured an interview at Y Combinator. We didn't advance past the interview, primarily because we were ex-airline employees pivoting into the ecommerce vendor space. However, we were proud to have made it that far, as only around 7% of applicants receive an interview.`, length: 140 },

      ]
    },
    {
      id: 'jetblue',
      name: 'JetBlue',
      timeFrame: 'Sept 2015 – Sept  2019',
      icon: jetblueIcon,
      positions: [{title: 'Frontend Engineer', timeFrame: 'Sept 2015 - Sept 2018'}, {title: 'Senior Frontend Engineer', timeFrame: 'Sept 2018 - Sept 2019'}],
      details: 'JetBlue is a major airline in the United States based in New York City. I joined as a Frontend Engineer working on JetBlue.com and crew member tools, then was promoted to Senior Frontend Engineer after leading the development of several internal projects.',
      position: 850,
      branches: [
        { name: 'Jetblue.com', img: jetblueImg, details: `The old JetBlue.com was outdated and slow. For every second it takes to load a webpage, JetBlue potentially loses millions of dollars as customers close the tab and book elsewhere. I worked on a host of features, including implementing server-side rendering to improve performance and building booking features in Angular.`, length: 180 },
        { name: 'Inflight Service Assistant (IFSA)', img: ifsaImg, details: `To eliminate paper manifests and other paper-based processes, IFSA was created to display the entire flight manifest showing who is on the flight and where they are seated, enable in-flight meal and drink purchases, and provide digital access to manuals that crew no longer needed to carry. This was built in Angular within an Apache Cordova container so it could run on iOS.`, length: 150 },
        { name: 'Blue Eye', img: blueEyeImg, details: `BlueEye is a project I led from beginning to end to deliver visibility into the entire operation. BlueEye provides full detail of a flight—from where the aircraft has been, to who is on it now, to where it's scheduled in four hours. This becomes essential when things don't go as planned. BlueEye was used daily by all of JetBlue's over 20,000 crew members and continues to power JetBlue's operations to this day.`, length: 170 }
      ]
    },
    {
      id: 'iheartmedia',
      name: 'iHeartMedia, Inc',
      icon: iheartmediaIcon,
      timeFrame: 'Oct 2014 – Sept 2015',
      positions: [{title: 'Software Engineer', timeFrame: 'Oct 2014 - Sept 2015'}],
      details: `iHeartMedia is a media company that owns a large share of local radio stations, including the most popular radio stations in most major metro areas. All those radio stations can be listened to on their app, iHeartRadio. I worked on the radio advertising tooling systems, which tracked analytics for music and advertisements and how they affected user listening behavior.`,
      position: 1000,
      branches: [
        { name: 'Media Montiors', img:  mmImg, details: `Media Monitors is a division within iHeartMedia that handles all radio advertising, analytics, and builds tools for radio stations. I worked on the RCS team to build better dashboards so radio stations could see which songs were performing well with their audiences and which advertisements were being listened to versus tuned out.`, length: 130 },
        { name: 'Overhauled testing Suite', img: rcsImg, details: `Media Monitors as well as RCS (another division within iHeartMedia) was plagued with flaky tests and bugs. I worked to overhaul the entire automated testing suite to be more reliable and efficient, which significantly reduced the number of bugs and flaky tests.`, length: 170 },
    
      ]
    },
    {
      id: 'con-edison',
      name: 'Con Edison Energy',
      timeFrame: 'May 2013 - Oct 2014',
      icon: conedisonIcon,
      positions: [{title: 'Software Engineer', timeFrame: 'May 2013 - Oct 2014'}],
      details: 'Con Edison is the major power company that supplies electricity to New York City and many surrounding counties. I joined as a Programmer Analyst and worked closely with their oil/natural gas trading and procurement floor.',
      position: 1200,
      branches: [
        { name: 'Oil Web Scrapper', img: oilWebScrapperImg, details:`I built a web scraper that scraped multiple websites for historical oil prices and weather patterns, then output that data into a database. This provided a holistic view of oil price data and enabled our traders to make better decisions when hedging their positions.`, length: 130 },
        { name: 'Allergro', img: allergroImg, details:`Allegro was the oil trading platform used by our traders to execute positions in oil and natural gas. I worked on building plugins and other features for the application to help traders work more efficiently.`, length: 170 },
        { name: 'Dodd Frank Compliance', img: doddFrankImg, details: `I worked on Dodd-Frank compliance, which required extensive documentation and reporting of all trades and positions. I built a system that automatically generated the required reports in the SEC-mandated format so they could be published to their API when required. I also created checks and verifications to ensure the data was accurate and compliant.`, length: 150 }
      ]
    }
  ];

  // Calculate viewport dimensions and main line path
  useEffect(() => {
    const calculateDimensions = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isMobileDevice = window.innerWidth <= 768;
      
      setViewportWidth(width);
      setViewportHeight(height);
      // On mobile, adjust viewBox to allow lines to extend above viewport
      const viewBoxY = isMobileDevice ? -height * 0.05 : 0;
      const viewBoxHeight = isMobileDevice ? height * 1.05 : height;
      setViewBox(`0 ${viewBoxY} ${width} ${viewBoxHeight}`);
      
      // Recreate original smooth S-curve in screen coordinates
      // Original: M-1000 0 C 2000 0, 400 150, 500 300 S 500 500, 500 700 L 500 2114
      
      const startX = -width * 0.2; // Start off-screen left (20% of width)
      // On mobile, start from top (0 or slightly negative) to extend fully
      // On desktop, start 5% down for breathing room
      const startY = isMobileDevice ? -height * 0.05 : height * 0.05;
      const finalX = width * 0.55; // Slightly off-center right (10-20% range)
      
      // First curve control points
      const control1X = width * 1.2; // Wide sweep
      const control1Y = startY; // Keep same Y as start for smooth horizontal curve
      const control2X = width * 0.4;
      const control2Y = height * 0.12; // ~12% down
      
      // First curve end point
      const curve1EndX = finalX;
      const curve1EndY = height * 0.19; // ~19% down
      
      // Second curve (S-curve) control point
      const control3X = finalX;
      const control3Y = height * 0.29; // ~29% down
      
      // Second curve end point (where vertical starts)
      const curve2EndX = finalX;
      const curve2EndY = height * 0.38; // ~38% down (matches original ~700/2114)
      
      // Smooth S-curve: Start off-screen (with padding), curve gracefully, then go vertical
      const path = `M${startX} ${startY} C ${control1X} ${control1Y}, ${control2X} ${control2Y}, ${curve1EndX} ${curve1EndY} S ${control3X} ${control3Y}, ${curve2EndX} ${curve2EndY} L ${finalX} ${height}`;
      
      setMainLinePath(path);
      
      console.log('Calculated main line path:', path);
      console.log('ViewBox:', `0 0 ${width} ${height}`);
    };
    
    calculateDimensions();
    
    // Add resize listener
    window.addEventListener('resize', calculateDimensions);
    return () => window.removeEventListener('resize', calculateDimensions);
  }, []);

  // Get branch line configuration for each station
  const getBranchLineConfig = (stationId, stationPos) => {
    if (!stationPos) return null;

    const { x, y } = stationPos;
    // Use SAME viewBox as main line (actual screen dimensions) for coordinate consistency
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isMobileDevice = window.innerWidth <= 768;
    // On mobile, start/end slightly above viewport to extend fully
    const mobileOffset = isMobileDevice ? -height * 0.05 : 0;
    // Adjust viewBox for mobile to allow lines to extend above
    const viewBoxY = isMobileDevice ? -height * 0.05 : 0;
    const viewBoxHeight = isMobileDevice ? height * 1.05 : height;
    const viewBoxStr = `0 ${viewBoxY} ${width} ${viewBoxHeight}`;

    console.log(`Creating branch line for ${stationId} at position:`, stationPos, 'Using ACTUAL screen dimensions:', width, height, 'Window:', window.innerWidth, window.innerHeight);

    const configs = {
      'herald-api': {
        // Vertical line from top to bottom through station
        d: `M${x} ${mobileOffset} L${x} ${height}`,
        color: '#017FC4', // Blue
        viewBox: viewBoxStr
      },
      'haven-technologies': {
        // Start top, go down through station, continue straight, then S-curve to exit right
        d: `M${x} ${mobileOffset} L${x} ${y} L${x} ${height * 0.7} Q${x + (width - x) * 0.3} ${height * 0.75}, ${x + (width - x) * 0.6} ${height * 0.8} T${width} ${height * 0.85}`,
        color: '#019A63', // Green
        viewBox: viewBoxStr
      },
      'alta-saas': {
        // Diagonal from bottom-left to top-right through station
        // Calculate to ensure it passes through (x, y)
        d: `M0 ${height} L${x} ${y} L${width} ${mobileOffset}`,
        color: '#FFD203', // Amber
        viewBox: viewBoxStr
      },
      'jetblue': {
        // Curved from top-left through station
        d: `M0 ${mobileOffset} Q${x * 0.5} ${y * 0.5}, ${x} ${y} T${width} ${height}`,
        color: '#AF378B', // Purple
        viewBox: viewBoxStr
      },
      'iheartmedia': {
        // Horizontal through station, then smooth curve vertical up off the page
        // Start from right, go horizontal through station, smooth curve to vertical up
        // Q command: control point at (x-40, y) curves to (x-40, y-40) for smooth transition
        d: `M${width} ${y} L${x} ${y} Q${x - 40} ${y} ${x - 40} ${y - 40} L${x - 40} ${mobileOffset}`,
        color: '#E70541', // Red
        viewBox: viewBoxStr
      },
      'con-edison': {
        // Curved from bottom-right through station
        d: `M${width} ${height} Q${x * 1.5} ${y * 1.5}, ${x} ${y} T0 ${mobileOffset}`,
        color: '#73AF2E', // Pink
        viewBox: viewBoxStr
      }
    };

    return configs[stationId] || null;
  };

  // Adjust label position on mobile to prevent off-screen labels
  const adjustLabelPosition = (stationId) => {
    if (!isMobile) return;
    
    const label = labelRefs.current[stationId];
    if (!label) return;

    // Reset to default positioning first
    label.style.left = '1.5rem';
    label.style.right = 'auto';
    label.style.marginLeft = '20px';
    label.style.marginRight = 'auto';

    // Get bounding rects after default positioning
    const labelRect = label.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    // Check if label goes off the right side of screen
    if (labelRect.right > viewportWidth - 10) {
      // Position label on the left side instead
      label.style.left = 'auto';
      label.style.right = '1.5rem';
      label.style.marginLeft = 'auto';
      label.style.marginRight = '20px';
    } else if (labelRect.left < 10) {
      // If label goes off the left, keep it on the right
      label.style.left = '1.5rem';
      label.style.right = 'auto';
      label.style.marginLeft = '20px';
      label.style.marginRight = 'auto';
    }
  };

  // Adjust hover card position to keep it in viewport
  const adjustHoverCardPosition = (stationId) => {
    const card = hoverCardRefs.current[stationId];
    if (!card) return;

    // Reset to default positioning first
    card.style.left = '1.5rem';
    card.style.right = 'auto';
    card.style.top = 'auto';
    card.style.bottom = '-0.5rem';
    card.style.position = 'absolute';
    // card.style.zIndex = '3001';

    // Get bounding rects after default positioning
    const cardRect = card.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let adjustedStyle = {};
    
    // Check vertical positioning
    if (cardRect.top < 10) {
      // Card goes off the top - position it below the station instead
      adjustedStyle.top = '-0.5rem';
      adjustedStyle.bottom = 'auto';
    } else if (cardRect.bottom > viewportHeight - 10) {
      // Card goes off the bottom - ensure it stays within viewport
      const overflow = cardRect.bottom - viewportHeight + 10;
      adjustedStyle.bottom = `${-0.5 + (overflow / 16)}rem`; // Convert px to rem
      adjustedStyle.top = 'auto';
    }
    
    // Check horizontal positioning
    if (cardRect.right > viewportWidth - 10) {
      // Card goes off the right - position it to the left of station instead
      adjustedStyle.left = 'auto';
      adjustedStyle.right = '1.5rem';
    } else if (cardRect.left < 10) {
      // Card goes off the left - keep it on the right but push it in
      adjustedStyle.left = '0.5rem';
    }
    
    // Apply adjusted styles
    Object.assign(card.style, adjustedStyle);
  };

  // Close all lines when switching from multi-mode to single-mode
  useEffect(() => {
    if (!allowMultipleLines) {
      // Close all lines immediately
      setExpandedStations(new Set());
      setActiveBranch(null);
      setActiveBranches(new Set());
      setVisibleBranchStations(new Set());
      setBranchStationPositions({});
    }
  }, [allowMultipleLines]);

  // Update mobile detection on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      // Adjust all label positions after resize
      if (window.innerWidth <= 768) {
        visibleStations.forEach(stationId => {
          setTimeout(() => adjustLabelPosition(stationId), 50);
        });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [visibleStations, isMobile]);

  // Adjust label positions when stations become visible on mobile
  useEffect(() => {
    if (isMobile) {
      visibleStations.forEach(stationId => {
        setTimeout(() => adjustLabelPosition(stationId), 100);
      });
    }
  }, [visibleStations, isMobile]);

  const toggleStation = (stationId) => {
    if (allowMultipleLines) {
      // Multi-mode: Allow multiple lines open at once
      setActiveBranches(prev => {
        const newSet = new Set(prev);
        if (newSet.has(stationId)) {
          newSet.delete(stationId);
        } else {
          newSet.add(stationId);
        }
        return newSet;
      });
      
      setExpandedStations(prev => {
        const newSet = new Set(prev);
        if (newSet.has(stationId)) {
          newSet.delete(stationId);
        } else {
          newSet.add(stationId);
        }
        return newSet;
      });
    } else {
      // Single-mode: Only one line at a time (current behavior)
      setExpandedStations(prev => {
        const newSet = new Set(prev);
        if (newSet.has(stationId)) {
          newSet.delete(stationId);
          setActiveBranch(null);
          setVisibleBranchStations(new Set());
          setBranchStationPositions({});
        } else {
          newSet.clear();
          newSet.add(stationId);
          setVisibleBranchStations(new Set());
          setBranchStationPositions({});
          setActiveBranch(stationId);
        }
        return newSet;
      });
    }
  };



  const draw = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: (i) => {
        const delay = i * 0.5
        return {
            pathLength: 1,
            opacity: 1,
            transition: {
                pathLength: { delay, type: "spring", duration: 20, bounce: 0 },
                opacity: { delay, duration: 20 },
            },
        }
    },
    exit: (i) => {
        const delay = i * 0.1
        return {
            pathLength: 0,
            opacity: 0,
            transition: {
                pathLength: { delay, type: "spring", duration: 15, bounce: 0 },
                opacity: { delay, duration: 15 },
            },
        }
    }
}

  // Handle station appearance during animation
  const handleStationTrigger = (stationData) => {
    console.log('Station should appear:', stationData);
    console.log('Adding station to visible set:', stationData.station.id);
    
    if(!visibleStations.has(stationData.station.id)){
      setVisibleStations(prev => {
        const newSet = new Set([...prev, stationData.station.id]);
        console.log('Visible stations after update:', Array.from(newSet));
        return newSet;
      });
      
      // Get container offset to convert screen coordinates to container-relative
      const containerEl = containerRef.current;
      const containerRect = containerEl ? containerEl.getBoundingClientRect() : { left: 0, top: 0 };
      
      // Calculate position relative to container
      // On mobile, the viewBox has a negative Y offset, but getScreenCTM() should account for this
      // However, we need to ensure the coordinates are correct
      let relativeX = stationData.x - containerRect.left;
      let relativeY = stationData.y - containerRect.top;
      
      // On mobile, if we're using SVG coordinates, we might need to adjust
      // But getScreenCTM() should already handle the viewBox offset correctly
      // The issue might be that we need to use the SVG coordinates directly
      if (isMobile && svgRef.current && stationData.svgX !== undefined && stationData.svgY !== undefined) {
        // Get the SVG element to check its viewBox
        const svgEl = svgRef.current;
        if (svgEl) {
          // Use SVG coordinates and convert using getScreenCTM
          const svgPoint = svgEl.createSVGPoint();
          svgPoint.x = stationData.svgX;
          svgPoint.y = stationData.svgY;
          const matrix = svgEl.getScreenCTM();
          if (matrix) {
            const screenPoint = svgPoint.matrixTransform(matrix);
            relativeX = screenPoint.x - containerRect.left;
            relativeY = screenPoint.y - containerRect.top;
          }
        }
      }
      
      console.log('Station position - Screen:', stationData.x, stationData.y, 'SVG:', stationData.svgX, stationData.svgY, 'Container offset:', containerRect.left, containerRect.top, 'Relative:', relativeX, relativeY);
   
      setMainStationPositions(prev => ({
        ...prev,
        [stationData.station.id]: {
          x: relativeX,
          y: relativeY
        }
      }));
    } else {
      console.log('Station already in visible set:', stationData.station.id);
    }
  };

  // Handle branch sub-station appearance during animation
  const handleBranchStationTrigger = (stationData) => {
    // The stationData.station.id already contains the full branch ID (e.g., "herald-api-branch-0")
    // Extract the parent station ID from it
    const parentStationId = stationData.station.id.split('-branch-')[0];
    const branchId = stationData.station.id;
    
    console.log('Branch station should appear:', branchId, 'Parent:', parentStationId, stationData);
    
    if(!visibleBranchStations.has(branchId)){
      setVisibleBranchStations(prev => {
        const newSet = new Set([...prev, branchId]);
        console.log('Visible branch stations after update:', Array.from(newSet));
        return newSet;
      });
      
      // Branch SVG viewBox matches container dimensions and is at (0,0) within container
      // So coordinates from getScreenCTM should already be container-relative
      // BUT - getScreenCTM returns SCREEN coordinates, so we still need offset
      const containerEl = containerRef.current;
      const containerRect = containerEl ? containerEl.getBoundingClientRect() : { left: 0, top: 0 };
      
      const relativeX = stationData.x - containerRect.left;
      const relativeY = stationData.y - containerRect.top;
      
      console.log('Branch station - Screen coords:', stationData.x, stationData.y, 'Container at:', containerRect.left, containerRect.top, 'Final:', relativeX, relativeY);
   
      setBranchStationPositions(prev => ({
        ...prev,
        [branchId]: {
          x: relativeX,
          y: relativeY
        }
      }));
    }
  };
  return (
    <div className="map" onClick={() => setBranchModalData(null)}>
      <div className="subway-container" ref={containerRef}>

{/* River - Behind everything */}
<svg 
  style={{
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
    pointerEvents: 'none',
    overflow: 'visible'
  }}
  viewBox={isMobile ? `0 ${-viewportHeight * 0.05} ${viewportWidth} ${viewportHeight * 1.05}` : `0 0 ${viewportWidth} ${viewportHeight}`}
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path 
    d={`M${viewportWidth * 0.91} ${-viewportHeight * 0.1} Q${viewportWidth * 0.75} ${viewportHeight * 0.17}, ${viewportWidth * 1.00} ${viewportHeight * 0.31} T${viewportWidth * 0.37} ${viewportHeight * 1.1}`}
    stroke="#9FC9EA" 
    strokeWidth="170"
  />
</svg>

{mainLinePath && (
  <SubwayLine 
    style={{
      position: 'absolute',
      // zIndex: '10'
    }}
    ref={svgRef}  
    draw={draw} 
    stroke="#F18401" 
    strokeWidth="8" 
    viewBox={viewBox}
    d={mainLinePath} 
    stations={stations}
    onStationTrigger={handleStationTrigger}
  />
)}

{/* Branch Lines with AnimatePresence */}
<AnimatePresence mode="wait">
  {allowMultipleLines ? (
    // Multi-mode: Render all active branches
    Array.from(activeBranches).map(branchId => {
      const stationPos = mainStationPositions[branchId];
      const branchConfig = getBranchLineConfig(branchId, stationPos);
      const currentStation = stations.find(s => s.id === branchId);
      
      if (!branchConfig || !currentStation) return null;

      const branchStations = currentStation.branches.map((branch, idx) => ({
        id: `${branchId}-branch-${idx}`,
        name: branch.name,
        ...branch
      }));
// , zIndex: 1
      return (
        <div key={branchId} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          <SubwayLine
            draw={draw}
            stroke={branchConfig.color}
            strokeWidth="8"
            d={branchConfig.d}
            viewBox={branchConfig.viewBox}
            stations={branchStations}
            onStationTrigger={handleBranchStationTrigger}
          />
        </div>
      );
    })
  ) : (
    // Single-mode: Render only activeBranch
    activeBranch && (() => {
      const stationPos = mainStationPositions[activeBranch];
      const branchConfig = getBranchLineConfig(activeBranch, stationPos);
      const currentStation = stations.find(s => s.id === activeBranch);
      
      if (!branchConfig || !currentStation) return null;

      const branchStations = currentStation.branches.map((branch, idx) => ({
        id: `${activeBranch}-branch-${idx}`,
        name: branch.name,
        ...branch
      }));
// , zIndex: 1
      return (
        <div key={activeBranch} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          <SubwayLine
            draw={draw}
            stroke={branchConfig.color}
            strokeWidth="8"
            d={branchConfig.d}
            viewBox={branchConfig.viewBox}
            stations={branchStations}
            onStationTrigger={handleBranchStationTrigger}
          />
        </div>
      );
    })()
  )}
</AnimatePresence>

{/* VISIBLE STATIONS:
{Array.from(visibleStations).map((station) => (
  <div key={station}>{station}</div>
))} */}
{/* -------------------------------- */}
{stations.map((station, index) => (
          <div key={station.id}>
            {/* {station.id} - has: {String(visibleStations.has(station.id))} - Y: {mainStationPositions[station.id]?.y}px - X: {mainStationPositions[station.id]?.x}px
            Main Station */}
           {visibleStations.has(station.id) && (
              <div 
                className="station" 
                style={{ 
                  top: mainStationPositions[station.id] ? `${mainStationPositions[station.id].y}px` : `${station.position}px`,
                  left: mainStationPositions[station.id] ? `${mainStationPositions[station.id].x}px` : '50%',
                  zIndex: hoveredStation === station.id ? 10000 : 10
                }}
                onClick={() => {
                  if (isMobile) {
                    // On mobile, open as full-screen modal
                    setMainStationModalData({
                      station: station,
                      lineColor: "#F18401"
                    });
                  } else {
                    toggleStation(station.id);
                  }
                }}
                onMouseEnter={() => {
                  if (!isMobile) {
                    setHoveredStation(station.id);
                    // Use setTimeout to ensure the card is rendered and visible before adjusting
                    setTimeout(() => adjustHoverCardPosition(station.id), 50);
                  }
                }}
                onMouseLeave={() => {
                  if (!isMobile) {
                    setHoveredStation(null);
                  }
                }}
              >
            
              <div className="station-circle">
                {/* Simple label - shows by default */}
                <span 
                  className="station-label"
                  ref={(el) => {
                    if (el) {
                      labelRefs.current[station.id] = el;
                      // Adjust position after ref is set
                      if (isMobile) {
                        setTimeout(() => adjustLabelPosition(station.id), 50);
                      }
                    }
                  }}
                >
                  {station.name}
                </span>
                
                {/* Hover card - shows on hover */}
                <StationHoverCard 
                  station={station}
                  ref={(el) => {
                    if (el) hoverCardRefs.current[station.id] = el;
                  }}
                  onImageZoom={setZoomedImage}
                  lineColor="#F18401"
                />
              </div>
     
              </div>
            )}

       
          </div>
        ))}

        {/* Render branch sub-stations */}
        {allowMultipleLines ? (
          // Multi-mode: Render sub-stations for all active branches
            Array.from(activeBranches).flatMap(branchId => {
              const currentStation = stations.find(s => s.id === branchId);
              if (!currentStation) return [];
              
              return currentStation.branches.map((branch, branchIndex) => {
                const subStationId = `${branchId}-branch-${branchIndex}`;
                const branchPos = branchStationPositions[subStationId];
                
                // Get the branch line color
                const stationPos = mainStationPositions[branchId];
                const branchConfig = getBranchLineConfig(branchId, stationPos);
                const lineColor = branchConfig?.color || '#F18401';
                
                // Create a station object for the hover card
                const branchStationData = {
                  id: subStationId,
                  name: branch.name,
                  img: branch.img || null,
                  timeFrame: branch.timeFrame || '',
                  details: branch.details || '',
                  isBranch: true
                };
                
                return (
                  visibleBranchStations.has(subStationId) && branchPos && (
                    <div
                      key={subStationId}
                      className="sub-station"
                      style={{
                        top: `${branchPos.y - 9.5}px`,
                        left: `${branchPos.x - 9.5}px`,
                        zIndex: clickedBranchStation === subStationId ? 10000 : 30 - branchIndex
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Open modal instead of inline card
                        setBranchModalData({
                          station: branchStationData,
                          lineColor: lineColor
                        });
                      }}
                    >
                      <div className="station-circle" style={{ 
                        width: '12px', 
                        height: '12px',
                        borderColor: '#000000',
                        backgroundColor: '#000000'
                      }}>
                        <span 
                          className="station-label"
                          ref={(el) => {
                            if (el && isMobile) {
                              const subStationId = `${branchId}-${branchIndex}`;
                              labelRefs.current[subStationId] = el;
                              setTimeout(() => {
                                const labelRect = el.getBoundingClientRect();
                                const viewportWidth = window.innerWidth;
                                if (labelRect.right > viewportWidth - 10) {
                                  el.style.left = 'auto';
                                  el.style.right = '1.5rem';
                                  el.style.marginLeft = 'auto';
                                  el.style.marginRight = '20px';
                                }
                              }, 50);
                            }
                          }}
                        >
                          {branch.name}
                        </span>
                      </div>
                    </div>
                  )
                );
              });
            })
        ) : (
          // Single-mode: Render sub-stations for activeBranch only
            activeBranch && stations.find(s => s.id === activeBranch)?.branches.map((branch, branchIndex) => {
              const branchId = `${activeBranch}-branch-${branchIndex}`;
              const branchPos = branchStationPositions[branchId];
              
              // Get the branch line color
              const stationPos = mainStationPositions[activeBranch];
              const branchConfig = getBranchLineConfig(activeBranch, stationPos);
              const lineColor = branchConfig?.color || '#F18401';
              
              // Create a station object for the hover card
              const branchStationData = {
                id: branchId,
                name: branch.name,
                img: branch.img || null,
                timeFrame: branch.timeFrame || '',
                details: branch.details || '',
                isBranch: true
              };
              
              return (
                visibleBranchStations.has(branchId) && branchPos && (
                  <div
                    key={branchId}
                    className="sub-station"
                    style={{
                      top: `${branchPos.y - 9.5}px`,
                      left: `${branchPos.x - 9.5}px`,
                      zIndex: clickedBranchStation === branchId ? 10000 : 30 - branchIndex
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Open modal instead of inline card
                      setBranchModalData({
                        station: branchStationData,
                        lineColor: lineColor
                      });
                    }}
                  >
                    <div className="station-circle" style={{ 
                      width: '12px', 
                      height: '12px',
                      borderColor: '#000000',
                      backgroundColor: '#000000'
                    }}>
                      <span 
                        className="station-label"
                        ref={(el) => {
                          if (el && isMobile) {
                            const subStationId = `${activeBranch}-${branchIndex}`;
                            labelRefs.current[subStationId] = el;
                            setTimeout(() => {
                              const labelRect = el.getBoundingClientRect();
                              const viewportWidth = window.innerWidth;
                              if (labelRect.right > viewportWidth - 10) {
                                el.style.left = 'auto';
                                el.style.right = '1.5rem';
                                el.style.marginLeft = 'auto';
                                el.style.marginRight = '20px';
                              }
                            }, 50);
                          }
                        }}
                      >
                        {branch.name}
                      </span>
                    </div>
                  </div>
                )
              );
            })
        )}

      </div>
      
      {/* Branch Modal - rendered at map level */}
      {branchModalData && (
        <div 
          className="branch-modal-overlay"
          onClick={() => setBranchModalData(null)}
        >
          <div 
            className="branch-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="branch-modal-close"
              onClick={() => setBranchModalData(null)}
              aria-label="Close"
            >
              ×
            </button>
            <StationHoverCard 
              station={branchModalData.station}
              onImageZoom={setZoomedImage}
              lineColor={branchModalData.lineColor}
            />
          </div>
        </div>
      )}

      {/* Main Station Modal - rendered at map level (mobile only) */}
      {mainStationModalData && (
        <div 
          className="branch-modal-overlay"
          onClick={() => {
            // When closing modal, animate the line in
            toggleStation(mainStationModalData.station.id);
            setMainStationModalData(null);
          }}
        >
          <div 
            className="branch-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="branch-modal-close"
              onClick={() => {
                // When closing modal, animate the line in
                toggleStation(mainStationModalData.station.id);
                setMainStationModalData(null);
              }}
              aria-label="Close"
            >
              ×
            </button>
            <StationHoverCard 
              station={mainStationModalData.station}
              onImageZoom={setZoomedImage}
              lineColor={mainStationModalData.lineColor}
            />
          </div>
        </div>
      )}

      {/* Image zoom overlay - rendered at map level */}
      {zoomedImage && (
        <div 
          className="image-zoom-overlay active"
          onClick={() => setZoomedImage(null)}
        >
          <div className="image-zoom-container">
            <button 
              className="image-zoom-close"
              onClick={() => setZoomedImage(null)}
              aria-label="Close"
            >
              ×
            </button>
            {/* Special case: Jetblue.com shows old image transitioning to new */}
            {zoomedImage.src === jetblueImg ? (
              <div className="jetblue-transition-container">
                <img src={jetblueImg} alt={zoomedImage.alt} className="jetblue-new-image" />
                <img src={jetblueImgOld} alt={zoomedImage.alt} className="jetblue-old-image" />
              </div>
            ) : (
              <img src={zoomedImage.src} alt={zoomedImage.alt} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Map;
